import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import * as $ from 'jquery';
import { CustomValidators } from 'src/app/shared/classes/CustomValidators';
import { Helper } from 'src/app/shared/classes/Helper';
import { HitApi } from 'src/app/shared/classes/HitApi';
import { Widget } from 'src/app/shared/classes/Widget';
import { WidgetInjectedData } from 'src/app/shared/classes/WidgetInjectedData';
import { ButtonColorType } from 'src/app/shared/enums/ButtonColorType';
import { FilterType } from 'src/app/shared/enums/FilterType';
import { FormState } from 'src/app/shared/enums/FormState';
import { IconType } from 'src/app/shared/enums/IconType';
import { ModalType } from 'src/app/shared/enums/ModalType';
import { IIntegrationApiResponse } from 'src/app/shared/interfaces/api/portlets/IIntegrationApiResponse';
import { IButtonGeneratorInput } from 'src/app/shared/interfaces/button-generator/IButtonGeneratorInput';
import { IFormField } from 'src/app/shared/interfaces/form-generator/IFormField';
import { IFormGeneratorInput } from 'src/app/shared/interfaces/form-generator/IFormGeneratorInput';
import { IIntegrationInput } from 'src/app/shared/interfaces/integration/IIntegrationInput';
import { IModalData } from 'src/app/shared/interfaces/modal/IModalData';
import { FormGeneratorModalComponent } from '../../../modal-templates/form-generator-modal/form-generator-modal.component';
import { ButtonType } from './../../../../enums/ButtonType';
import { IntegrationCacheService } from './../../../../services/cache/integration-cache/integration-cache.service';

@Component({
    selector: 'app-jiradesk-integration',
    templateUrl: './jiradesk-integration.component.html',
    styleUrls: ['./jiradesk-integration.component.sass']
})
export class JiradeskIntegrationComponent implements OnInit, OnDestroy {
    integrationInput: IIntegrationInput = null;
    widgetRef: Widget;
    isParentIntegrated = false;
    isSelfIntegrated = false;
    isConfigured: Boolean = false;
    formGenInput: IFormGeneratorInput;

    constructor(
        widgetData: WidgetInjectedData,
        private integrationCacheService: IntegrationCacheService
    ) {
        this.widgetRef = widgetData.widgetRef;
    }

    ngOnInit(): void {
        this.setUpBasics();
    }

    setUpBasics() {
        this.integrationInput = {
            integrationName: this.widgetRef.widgetData.widgetInfo.text,
            integrationCategory: 'ITSM Integration',
            imageUrl: 'assets/integrations/jiradesk_2.png',
            buttons: [],
            imagePadding: 6
        };
        const loadedScript = this.integrationCacheService.jiradeskScript;
        const showJd = this.integrationCacheService.showJiradeskScript;
        if (loadedScript && showJd) {
            this.integrationCacheService.showJiradeskScript = false;
            this.loadScript(loadedScript);
        }
        window.onbeforeunload = () => {
            if (!this.integrationCacheService.showJiradeskScript) {
                this.integrationCacheService.jiradeskScript = null;
            }
        };
    }

    generateButtons(response: IIntegrationApiResponse) {
        this.formGenInput = {
            formName: 'Jiradesk',
            state: this.isSelfIntegrated ? FormState.EDIT : FormState.CREATE,
            submitButton: {
                buttonName: this.isSelfIntegrated ? 'Update' : 'Integrate',
                buttonType: ButtonType.FLAT,
                buttonColorType: ButtonColorType.PRIMARY,
                showLoader: true,
                function: (
                    buttonRef: IButtonGeneratorInput,
                    formGroup: FormGroup,
                    modalId: Symbol
                ) => {
                    if (formGroup.valid) {
                        buttonRef.loader = true;
                        if (this.isSelfIntegrated) {
                            // Update
                            const args = Helper.generateHitApiConfig(
                                this.widgetRef.widgetData.widgetInfo.update
                            );
                            args.input = {
                                ...formGroup.value,
                                integrationType: 'JIRADESK'
                            };
                            args.function = (response) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Jiradesk configuration updated successfully'
                                );
                                buttonRef.loader = false;
                                this.widgetRef.refreshWidget();
                                this.widgetRef.modalService.closeModal(
                                    null,
                                    modalId
                                );
                                $('#jsd-widget').hide();
                            };
                            args.errorFunction = (error) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Error updating jiradesk configuration'
                                );
                                buttonRef.loader = false;
                            };
                            new HitApi(
                                args,
                                this.widgetRef.httpService,
                                this.widgetRef.ngZone
                            ).hitApi();
                        } else {
                            // Create
                            const args = Helper.generateHitApiConfig(
                                this.widgetRef.widgetData.widgetInfo.create
                            );
                            args.input = {
                                ...formGroup.value,
                                integrationType: 'JIRADESK'
                            };
                            args.function = (response) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Jiradesk integrated successfully'
                                );
                                buttonRef.loader = false;
                                this.widgetRef.refreshWidget();
                                this.widgetRef.modalService.closeModal(
                                    null,
                                    modalId
                                );
                            };
                            args.errorFunction = (error) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Error integrating jiradesk'
                                );
                                buttonRef.loader = false;
                            };
                            new HitApi(
                                args,
                                this.widgetRef.httpService,
                                this.widgetRef.ngZone
                            ).hitApi();
                        }
                    }
                }
            },
            fields: [
                {
                    name: 'email',
                    placeholder: 'Email',
                    label: 'Email',
                    fieldType: FilterType.EMAIL,
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'Email is required'
                        },
                        {
                            validator: CustomValidators.email,
                            errorMessage: 'Invalid email'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['email']
                        : ''
                },
                {
                    name: 'supportedEmails',
                    placeholder: 'Support Email',
                    label: 'Support Email',
                    fieldType: FilterType.MATCHIPLIST,
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'Support email is required'
                        },
                        {
                            validator: CustomValidators.matChipAllowEmail,
                            errorMessage: 'Invalid email'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['supportedEmails']
                        : ''
                },
                {
                    name: 'apiKey',
                    placeholder: 'API Key',
                    label: 'API Key',
                    fieldType: FilterType.TEXT,
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    suffixIcon: {
                        hoverText: `Where to get API Key`,
                        iconData: {
                            type: IconType.MATICON,
                            class: 'help'
                        },
                        function: (field: IFormField, formGroup: FormGroup) => {
                            this.widgetRef.modalService.openInfoModal({
                                infoHeading: 'Your API Key',
                                content: [
                                    {
                                        type: 'ORDERED_LIST',
                                        data: [
                                            `Log in to https://id.atlassian.com/manage/api-tokens.`,
                                            `Click Create API token.`,
                                            `From the dialog that appears, enter a memorable and concise Label for your token and click Create.`,
                                            `Click Copy to clipboard, then paste the token to your script, or elsewhere to save.`
                                        ]
                                    }
                                ]
                            });
                        }
                    },
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'API key is required'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['apiKey']
                        : ''
                },
                {
                    name: 'domainName',
                    placeholder: 'Domain Name',
                    label: 'Domain Name',
                    fieldType: FilterType.TEXT,
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'Domain name is required'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['domainName']
                        : ''
                },
                {
                    name: 'widgetCode',
                    placeholder: 'Widget Code',
                    label: 'Widget Code',
                    fieldType: FilterType.TEXTAREA,
                    extraClass: 'tw-items-center',
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    suffixIcon: {
                        hoverText: `Where to get widget code`,
                        iconData: {
                            type: IconType.MATICON,
                            class: 'help'
                        },
                        function: (field: IFormField, formGroup: FormGroup) => {
                            this.widgetRef.modalService.openInfoModal({
                                infoHeading: 'Your Widget Code',
                                content: [
                                    {
                                        type: 'ORDERED_LIST',
                                        data: [
                                            `From your service desk project, go to Project settings > Widget`,
                                            `Choose the widget options that fit your needs.`,
                                            `Select Save to confirm your widget settings.`
                                        ]
                                    }
                                ]
                            });
                        }
                    },
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'Widget code is required'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['widgetCode']
                        : ''
                }
            ],
            extraClass: 'integration-styling add-scroll'
        };
        if (this.isSelfIntegrated || this.isParentIntegrated) {
            if (this.isSelfIntegrated) {
                // Adding play button
                this.integrationInput.buttons.push({
                    buttonName: 'Test',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-play'
                    },
                    buttonType: ButtonType.CIRCLE,
                    buttonColorType: ButtonColorType.SUCCESS,
                    hoverText: 'Test Jiradesk',
                    function: (buttonRef: IButtonGeneratorInput) => {
                        const scriptToRender = this.isSelfIntegrated
                            ? response.self.widgetCode
                            : this.isParentIntegrated
                            ? response.parent.widgetCode
                            : null;
                        const loadedScript =
                            this.integrationCacheService.jiradeskScript;

                        this.integrationCacheService.jiradeskScript =
                            scriptToRender;

                        if (loadedScript && loadedScript === scriptToRender) {
                            $('#jsd-widget').show();
                            return;
                        } else if (loadedScript) {
                            this.integrationCacheService.showJiradeskScript =
                                true;
                            window.location.reload();
                            return;
                        }

                        this.loadScript(scriptToRender);
                    }
                });

                // Adding edit button
                this.integrationInput.buttons.push({
                    buttonName: 'Edit',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-wrench'
                    },
                    buttonType: ButtonType.CIRCLE,
                    buttonColorType: ButtonColorType.SUCCESS,
                    hoverText: 'Edit Jiradesk',
                    function: () => {
                        const modalArgs: IModalData = {
                            modalName: 'Edit Jiradesk',
                            sourceId:
                                this.widgetRef.widgetData
                                    .widgetUniqueIdentifier,
                            modalType: ModalType.MIDDLE,
                            modalIcon: {
                                type: IconType.FONTAWSOME,
                                class: 'fas fa-wrench'
                            },
                            extraStepContainerClass: 'remove-form-space',
                            modalSteps: [
                                {
                                    stepName: 'Edit Jiradesk',
                                    resetModal: true,
                                    stepData: {
                                        componentToLoad:
                                            FormGeneratorModalComponent,
                                        payload: {
                                            data: this.formGenInput
                                        }
                                    }
                                }
                            ],
                            modalWidthVw: 60,
                            modalHeightVh: 70,
                            noBottomPadding: true
                        };
                        this.widgetRef.modalService.openModal(modalArgs);
                    }
                });

                // Adding delete button
                this.integrationInput.buttons.push({
                    buttonName: 'Delete',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-trash'
                    },
                    hoverText: 'Remove Jiradesk',
                    buttonType: ButtonType.CIRCLE,
                    buttonColorType: ButtonColorType.WARN,
                    function: (modalId: Symbol) => {
                        // Delete integration
                        const args = Helper.generateHitApiConfig(
                            this.widgetRef.widgetData.widgetInfo.delete
                        );
                        args.input = {
                            integrationType: 'JIRADESK'
                        };
                        args.function = (response) => {
                            this.widgetRef.notificationsService.showSnackBar(
                                `Jiradesk integration removed successfully`
                            );
                            this.widgetRef.modalService.closeModal(
                                null,
                                modalId
                            );
                            $('#jsd-widget').hide();
                            this.widgetRef.refreshWidget();
                        };
                        args.errorFunction = (error) => {
                            this.widgetRef.notificationsService.showSnackBar(
                                error.error.message,
                                true
                            );
                            this.widgetRef.modalService.closeModal(
                                null,
                                modalId
                            );
                        };
                        new HitApi(
                            args,
                            this.widgetRef.httpService,
                            this.widgetRef.ngZone
                        ).hitApi();
                    },
                    executeFunctionAfterConfirmation: {
                        modalName: 'Remove Jiradesk Integration',
                        modalIcon: {
                            type: IconType.FONTAWSOME,
                            class: 'far fa-trash-alt'
                        },
                        contextIcon: {
                            extraClass: 'color-accent',
                            type: IconType.FONTAWSOME,
                            class: 'fas fa-exclamation-triangle'
                        },
                        confirmationMessage: `Are you sure you want to remove jiradesk ?`,
                        buttonText: 'Remove',
                        buttonColorType: ButtonColorType.WARN,
                        loaderOnExec: true
                    }
                });
                this.isConfigured = true;
                return;
            }
        }
    }
    onIntegrate(formGenInput) {
        const modalArgs: IModalData = {
            modalName: 'Integrate Jiradesk',
            sourceId: this.widgetRef.widgetData.widgetUniqueIdentifier,
            modalType: ModalType.MIDDLE,
            modalIcon: {
                type: IconType.FONTAWSOME,
                class: 'fas fa-ticket-alt'
            },
            extraStepContainerClass: 'remove-form-space',
            modalSteps: [
                {
                    stepName: 'Integrate Jiradesk',
                    resetModal: true,
                    stepData: {
                        componentToLoad: FormGeneratorModalComponent,
                        payload: {
                            data: formGenInput
                        }
                    }
                }
            ],
            modalWidthVw: 60,
            modalHeightVh: 70,
            noBottomPadding: true
        };
        this.widgetRef.modalService.openModal(modalArgs);
    }
    bindData(response: IIntegrationApiResponse): void {
        if ('integration' in response && !response.integration) {
            this.isParentIntegrated = false;
            this.isSelfIntegrated = false;
        }
        if (response.parent && response.parent.integration) {
            this.isParentIntegrated = true;
            this.isSelfIntegrated = false;
        }
        if (response.self && response.self.integration) {
            this.isSelfIntegrated = true;
        }
        this.integrationInput.buttons = [];
        this.generateButtons(response);
        this.widgetRef.endLoader();
    }

    loadScript(widgetCode) {
        const node = document.createElement('script');
        node.type = 'text/javascript';
        node.async = true;
        node.id = 'ji-snippet-injector';
        const attributes = Object.keys(
            $(
                new DOMParser().parseFromString(widgetCode, 'text/html')
                    .firstChild
            )
                .children('head')
                .children('script')[0]['attributes']
        );
        for (let i = 0, length = attributes.length; i < length; ++i) {
            node.setAttribute(
                $(
                    new DOMParser().parseFromString(widgetCode, 'text/html')
                        .firstChild
                )
                    .children('head')
                    .children('script')[0]['attributes'][attributes[i]]['name'],
                $(
                    new DOMParser().parseFromString(widgetCode, 'text/html')
                        .firstChild
                )
                    .children('head')
                    .children('script')[0]['attributes'][attributes[i]]['value']
            );
        }
        document.getElementsByTagName('head')[0].appendChild(node);
        node.onload = () => {
            const DOMContentLoaded_event = document.createEvent('Event');
            DOMContentLoaded_event.initEvent('DOMContentLoaded', true, true);
            window.document.dispatchEvent(DOMContentLoaded_event);
        };
    }

    ngAfterViewInit(): void {
        this.widgetRef.setBindData(this.bindData.bind(this));
    }

    ngOnDestroy() {
        const loadedScript = this.integrationCacheService.jiradeskScript;
        if ($('#jsd-widget')) {
            $('#jsd-widget').hide();
        }
    }
}
